import api from './api';
import { cachedGet } from '../utils/lookupCache';
import { msalInstance } from './authConfig';

export interface UserSetting {
  id: number;
  user_email: string;
  setting_key: string;
  setting_value: any;
  created_at: string;
  updated_at: string;
}

/** The user_settings keys the sources endpoint allow-lists (mirrors PANEL_LAYOUT_SETTING_KEYS on the backend). */
export type PanelLayoutSettingKey = 'lc_dashboard_panels' | 'accounting_dashboard_panels';

/** A colleague's dashboard count-panel layout, offered as a source to copy from. */
export interface PanelLayoutSource {
  user_email: string;
  display_name: string;
  updated_at: string;
  workspace_count: number;
  panel_count: number;
  /** { [workspaceId]: PanelLayoutItem[] } — geometry only, no trash history. */
  panel_layouts: Record<string, any[]>;
}
/** @deprecated Use PanelLayoutSource. Kept so LCDashboards compiles unchanged. */
export type LcPanelLayoutSource = PanelLayoutSource;

class UserSettingsService {
  /**
   * Check if a user is authenticated
   */
  isAuthenticated(): boolean {
    const accounts = msalInstance.getAllAccounts();
    return accounts && accounts.length > 0;
  }

  /**
   * Get the current user's email from MSAL
   */
  private getUserEmail(): string {
    const accounts = msalInstance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      return accounts[0].username; // This is the email
    }
    throw new Error('No authenticated user found');
  }

  /**
   * Per-user localStorage cache key for a TanTable's settings.
   * Namespaced by email so a shared browser can't leak another user's layout.
   * Falls back to a non-namespaced key when no user is authenticated.
   */
  getTableCacheKey(settingsKey: string): string {
    try {
      const email = this.getUserEmail();
      return `tantable_cache_${email}_${settingsKey}`;
    } catch {
      return `tantable_cache_${settingsKey}`;
    }
  }

  /**
   * Per-user localStorage cache key for an arbitrary setting (generalizes
   * getTableCacheKey). Namespaced by email so a shared browser can't leak
   * another user's prefs; falls back to a non-namespaced key when unauthenticated.
   */
  getCacheKey(settingKey: string): string {
    try {
      const email = this.getUserEmail();
      return `usersetting_cache_${email}_${settingKey}`;
    } catch {
      return `usersetting_cache_${settingKey}`;
    }
  }

  /**
   * Get all user settings or a specific setting by key
   */
  async getUserSettings(settingKey?: string): Promise<UserSetting[]> {
    const userEmail = this.getUserEmail();
    const params = settingKey ? { setting_key: settingKey } : {};

    const response = await api.get('/settings/user-settings', {
      params,
      headers: {
        'X-User-Email': userEmail,
      },
    });
    return response.data;
  }

  /**
   * Requests currently in flight, keyed by setting key.
   *
   * Independent components routinely ask for the same setting while a page is
   * mounting — At Risk Monitor fetched `at_risk_monitor_filters` twice on every
   * load, and layout/theme/nav keys are read by whichever components happen to
   * mount. Each of those is a full round trip, and every request carries fixed
   * auth cost, so the duplicates are not free.
   *
   * This coalesces OVERLAP only; entries are dropped the moment the request
   * settles. It is deliberately not a result cache — a save followed by a
   * re-read must see the new value.
   */
  private inFlightGets = new Map<string, Promise<UserSetting | null>>();

  /**
   * Get a specific user setting by key
   */
  async getUserSetting(settingKey: string, signal?: AbortSignal): Promise<UserSetting | null> {
    // A caller that can abort owns its own request — sharing would let its
    // cancellation kill a sibling caller's response.
    if (signal) {
      return this.fetchUserSetting(settingKey, signal);
    }

    const existing = this.inFlightGets.get(settingKey);
    if (existing) {
      return existing;
    }

    const request = this.fetchUserSetting(settingKey).finally(() => {
      this.inFlightGets.delete(settingKey);
    });
    this.inFlightGets.set(settingKey, request);
    return request;
  }

  private async fetchUserSetting(settingKey: string, signal?: AbortSignal): Promise<UserSetting | null> {
    try {
      const userEmail = this.getUserEmail();
      const response = await api.get(`/settings/user-settings/${settingKey}`, {
        headers: {
          'X-User-Email': userEmail,
        },
        signal,
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create or update a user setting
   */
  async saveUserSetting(settingKey: string, settingValue: any): Promise<UserSetting> {
    const userEmail = this.getUserEmail();
    const response = await api.put(
      `/settings/user-settings/${settingKey}`,
      settingValue,
      {
        headers: {
          'X-User-Email': userEmail,
        },
      }
    );
    return response.data;
  }

  /**
   * Delete a user setting
   */
  async deleteUserSetting(settingKey: string): Promise<void> {
    const userEmail = this.getUserEmail();
    await api.delete(`/settings/user-settings/${settingKey}`, {
      headers: {
        'X-User-Email': userEmail,
      },
    });
  }

  /**
   * Convenience method: Get dashboard layout (DEPRECATED - use getDashboardLayoutNew)
   */
  async getDashboardLayout(dashboardName: string): Promise<any | null> {
    const setting = await this.getUserSetting(`dashboard_layout_${dashboardName}`);
    return setting ? setting.setting_value : null;
  }

  /**
   * Convenience method: Save dashboard layout (DEPRECATED - use saveDashboardLayoutNew)
   */
  async saveDashboardLayout(dashboardName: string, layout: any): Promise<UserSetting> {
    return await this.saveUserSetting(`dashboard_layout_${dashboardName}`, layout);
  }

  /**
   * Colleagues whose count-panel layout under `settingKey` can be copied, with their
   * panel geometry so the caller can filter out anyone still on the code default and
   * apply a choice without a second request. The server allow-lists the key; excludes
   * the caller; never includes anyone's panelTrash.
   */
  async getPanelLayoutSources(settingKey: PanelLayoutSettingKey): Promise<PanelLayoutSource[]> {
    const response = await api.get('/settings/lc-panel-layouts/sources', {
      params: { setting_key: settingKey },
    });
    return response.data;
  }

  /** LC Dashboards' sources — the original caller, kept as-is. */
  async getLcPanelLayoutSources(): Promise<PanelLayoutSource[]> {
    return this.getPanelLayoutSources('lc_dashboard_panels');
  }

  /**
   * Get dashboard layout from new dedicated endpoint
   */
  async getDashboardLayoutNew(dashboardType: string): Promise<any | null> {
    try {
      const userEmail = this.getUserEmail();
      const response = await api.get(`/settings/dashboard-layout/${dashboardType}`, {
        headers: {
          'X-User-Email': userEmail,
        },
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Save dashboard layout to new dedicated endpoint
   */
  async saveDashboardLayoutNew(dashboardType: string, layout: any): Promise<void> {
    const userEmail = this.getUserEmail();
    await api.put(
      `/settings/dashboard-layout/${dashboardType}`,
      layout,
      {
        headers: {
          'X-User-Email': userEmail,
        },
      }
    );
  }

  /**
   * Delete dashboard layout from new dedicated endpoint
   */
  async deleteDashboardLayoutNew(dashboardType: string): Promise<void> {
    const userEmail = this.getUserEmail();
    await api.delete(`/settings/dashboard-layout/${dashboardType}`, {
      headers: {
        'X-User-Email': userEmail,
      },
    });
  }

  /**
   * Get soft-deleted (trashed) workspaces/cards for a dashboard type
   */
  async getDashboardTrash(dashboardType: string): Promise<any[]> {
    const userEmail = this.getUserEmail();
    const response = await api.get(
      `/settings/dashboard-layout/${dashboardType}/trash`,
      {
        headers: {
          'X-User-Email': userEmail,
        },
      }
    );
    return response.data;
  }

  /**
   * Restore a soft-deleted workspace from trash
   */
  async restoreFromTrash(dashboardType: string, workspaceId: string): Promise<void> {
    const userEmail = this.getUserEmail();
    await api.put(
      `/settings/dashboard-layout/${dashboardType}/restore`,
      { workspace_id: workspaceId },
      {
        headers: {
          'X-User-Email': userEmail,
        },
      }
    );
  }

  /**
   * Get multiple user settings in a single request by specific keys
   */
  async getBatchUserSettings(keys: string[]): Promise<Record<string, any>> {
    const userEmail = this.getUserEmail();
    const response = await api.post('/settings/user-settings/batch', {
      keys
    }, {
      headers: {
        'X-User-Email': userEmail,
      },
    });
    return response.data.settings || {};
  }

  /**
   * Get multiple user settings in a single request by key prefix
   * Useful for fetching all table settings for a dashboard at once
   */
  async getUserSettingsByPrefix(prefix: string): Promise<Record<string, any>> {
    const userEmail = this.getUserEmail();
    const response = await api.post('/settings/user-settings/batch', {
      prefix
    }, {
      headers: {
        'X-User-Email': userEmail,
      },
    });
    return response.data.settings || {};
  }

  // ===========================================================================
  // Admin-managed DEFAULT table settings.
  //
  // These are the system defaults for shared/locked buckets, keyed by the same
  // settingsKey a TanTable uses (e.g. 'dispatch-card-<bucket>'). They seed a
  // table for users with no saved settings of their own and are the target the
  // "Reset all settings" action restores to. Reads are available to any
  // dashboard user; writes are gated by the 'edit-default-table-settings'
  // component permission (enforced server-side).
  // ===========================================================================

  /**
   * Batch-fetch admin default layouts by key prefix.
   * Mirrors getUserSettingsByPrefix so a dashboard can fetch all bucket defaults
   * in one request alongside the per-user settings.
   */
  async getDefaultSettingsByPrefix(prefix: string): Promise<Record<string, any>> {
    const response = await api.post('/settings/default-settings/batch', { prefix });
    return response.data.settings || {};
  }

  /**
   * Get the admin default layout for a single table key, or null if none exists.
   */
  async getDefaultSetting(settingKey: string, signal?: AbortSignal): Promise<any | null> {
    try {
      const response = await api.get(`/settings/default-settings/${settingKey}`, { signal });
      return response.data ? response.data.setting_value : null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Same as getDefaultSetting, but shared through the 5-minute lookup cache.
   * TanTable calls this for itself when the host page passes no `defaultSettings`,
   * so a dashboard rendering many grids coalesces into one request per key
   * instead of one per grid per mount.
   */
  async getDefaultSettingCached(settingKey: string, signal?: AbortSignal): Promise<any | null> {
    try {
      const response = await cachedGet<any>(`/settings/default-settings/${settingKey}`, { signal });
      return response.data ? response.data.setting_value : null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Publish (create or update) the admin default layout for a table key.
   * Requires the 'edit-default-table-settings' component permission.
   */
  async saveDefaultSetting(settingKey: string, settingValue: any): Promise<void> {
    await api.put(`/settings/default-settings/${settingKey}`, settingValue);
  }

  /**
   * Remove the published default for a table key, reverting to the code default.
   */
  async deleteDefaultSetting(settingKey: string): Promise<void> {
    await api.delete(`/settings/default-settings/${settingKey}`);
  }
}

export default new UserSettingsService();
